import { notifyError } from 'config/setupBugsnag';
import RestUtility from '../../restUtility';
import {
  accountEndPoint,
  iteroEndpoint,
} from '../../restUtility/restEndpoints';
import {
  AccountAddressAttr,
  AccountAddressesResponse,
  AccountResponse,
  Address,
  DoctorAddress,
  DownloadClientResponse,
  DownloadServerResponse,
  IOrder,
  IOrdersWithMetaData,
  IteroOrderDetails,
  OrderDetails,
  OrderLineItem,
  OrderRowData,
  IteroOrderRowData,
  IteroOrdersWithMetaData,
  IteroOrder,
} from '../types';

export class AccountService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async getDoctorOrderHistory(historyFilter: {
    shipmentStatusFilters: string[];
    pageParam?: number;
  }) {
    try {
      let shipmentStatusFiltersParam = '';
      if (historyFilter.shipmentStatusFilters.length > 0) {
        shipmentStatusFiltersParam = `filter[shipment_state]=${historyFilter.shipmentStatusFilters.join(
          ','
        )}`;
      }
      const cartFields =
        'number,completed_at,display_total,shipment_state,shipments,subscription_numbers';
      const response = await this.restUtility.get<IOrdersWithMetaData>(
        `${accountEndPoint}/orders?include=shipments&&fields[cart]=${cartFields}&${shipmentStatusFiltersParam}&page=${historyFilter.pageParam}&per_page=10`
      );

      return this.buildOrderHistoryForClient(response, historyFilter.pageParam);
    } catch (error) {
      notifyError('ApiError:: getDoctorOrderHistory', error);
      throw error;
    }
  }

  async getDoctorIteroOrderHistory(historyFilter: { pageParam?: number }) {
    try {
      const proposalFields =
        'salesforce_id,name,display_total_with_currency,shipped_at,tracking_number,status,completed_at';
      const response = await this.restUtility.get<IteroOrdersWithMetaData>(
        `${iteroEndpoint}/orders?page=${historyFilter.pageParam}&per_page=10&fields[proposal]=${proposalFields}`
      );

      return this.buildITeroOrderHistoryForClient(
        response,
        historyFilter.pageParam
      );
    } catch (error) {
      notifyError('ApiError:: getDoctorIteroOrderHistory', error);
      throw error;
    }
  }

  async getITeroOrderDetails(orderNumber: string) {
    try {
      const response = await this.restUtility.get<IteroOrder>(
        `${iteroEndpoint}/orders/${orderNumber}?include=billing_address,shipping_address&fields[proposal]=salesforce_id,name,billing_address,shipping_address,shipped_at,tracking_number,status,completed_at,order_payment_token,parsed_response,contract_document_url,estimated_monthly_payment,order_subscription_token`
      );

      return this.buildProposalForClient(response);
    } catch (error) {
      notifyError('ApiError:: getOrderDetails', error);
      throw error;
    }
  }

  async getDownloadsHistory(pageParam?: number) {
    try {
      const urlParams = `page=${pageParam}&per_page=10`;

      const response = await this.restUtility.get<DownloadServerResponse>(
        `${accountEndPoint}/downloads?&${urlParams}`
      );

      return this.buildDownloadHistoryForClient(response, pageParam);
    } catch (error) {
      notifyError('ApiError:: getDownloadsHistory', error);
      throw error;
    }
  }

  async getOrderDetails(orderNumber: string) {
    try {
      const response = await this.restUtility.get<IOrder>(
        `${accountEndPoint}/orders/${orderNumber}?include=line_items,variants,billing_address,shipping_address,payments.source,shipments&fields[variant]=discontinue_on`
      );

      return this.buildOrderDetailsForClient(response);
    } catch (error) {
      notifyError('ApiError:: getOrderDetails', error);
      throw error;
    }
  }

  async getDoctorAddresses() {
    try {
      const doctorAddress: DoctorAddress = {
        billingAddresses: [],
        shippingAddresses: [],
        defaultBillingAddress: {} as Address,
        defaultShippingAddress: {} as Address,
      };
      const response = await this.restUtility.get<AccountAddressesResponse>(
        `${accountEndPoint}/addresses `
      );
      const addresses = response.data.map(address =>
        this.buildAddress(address)
      );
      doctorAddress.billingAddresses = addresses.filter(
        address => address.billTo
      );
      doctorAddress.shippingAddresses = addresses.filter(
        address => address.shipTo
      );
      doctorAddress.defaultBillingAddress =
        doctorAddress.billingAddresses.filter(
          address => address.isDefaultBilling
        )[0];
      doctorAddress.defaultShippingAddress =
        doctorAddress.shippingAddresses.filter(
          address => address.isDefaultShipping
        )[0];
      return doctorAddress;
    } catch (error) {
      notifyError('ApiError:: getDoctorAddresses', error);
      throw error;
    }
  }

  async getAccountDetails() {
    try {
      const response = await this.restUtility.get<AccountResponse>(
        `${accountEndPoint}`
      );

      return this.buldAccountDetails(response);
    } catch (error) {
      notifyError('ApiError:: getAccountDetails', error);
      throw error;
    }
  }

  async updateEmailSubscription(subscribeOption: {
    user: { opt_in: boolean };
  }) {
    try {
      await this.restUtility.patch<IOrder>(
        `${accountEndPoint}?fields[user]=email,opt_in`,
        subscribeOption
      );
    } catch (error) {
      notifyError('ApiError:: updateEmailSubscription', error);
      throw error;
    }
  }

  buildOrderDetailsForClient(response: IOrder) {
    const orderDetails: OrderDetails = {} as OrderDetails;
    orderDetails.orderNumber = response.data.attributes.number;
    orderDetails.currency = response.data.attributes.currency;
    orderDetails.orderDate = response.data.attributes.completed_at;
    orderDetails.orderTime = response.data.attributes.completed_at;

    orderDetails.subTotal = response.data.attributes.display_item_total;
    orderDetails.tax = response.data.attributes.display_tax_amount; // not working
    orderDetails.withholdingTax =
      response.data.attributes.withholding_tax_amount ?? '';
    orderDetails.shippingCost = response.data.attributes.display_ship_total;
    orderDetails.isBulkDiscountEligible =
      response.data.attributes.is_bulk_discount_eligible;

    orderDetails.bulkDiscountTotal =
      response.data.attributes.bulk_discount_amount;
    orderDetails.displayBulkDiscountTotal =
      response.data.attributes.display_bulk_discount_amount;
    orderDetails.total = response.data.attributes.display_total;

    orderDetails.promoTotal = response.data.attributes.promo_total;
    orderDetails.displayPromoTotal =
      response.data.attributes.display_promo_total;

    orderDetails.dh_project_id = response.data.attributes.dh_project_id;

    const billingAddressId =
      response.data.relationships.billing_address.data.id;
    const billingAddress = response.included?.filter(
      includedItem =>
        includedItem.type === 'address' && includedItem.id === billingAddressId
    )[0];

    const shippingAddressId =
      response.data.relationships.shipping_address.data.id;
    const shippingAddress = response.included?.filter(
      includedItem =>
        includedItem.type === 'address' && includedItem.id === shippingAddressId
    )[0];

    orderDetails.status = response.data.attributes.shipment_state;
    orderDetails.billingAddress = this.buildAddress(billingAddress);
    orderDetails.shippingAddress = this.buildAddress(shippingAddress);
    orderDetails.lineItems = this.buildLineItems(response);

    const creditCardDetails = response.included?.filter(
      includedItem => includedItem.type === 'credit_card'
    )[0];
    orderDetails.creditCardType = creditCardDetails?.attributes?.cc_type;
    orderDetails.creditCardLast4Digits =
      creditCardDetails?.attributes?.card_number ?? '';
    orderDetails.lineItemSavings =
      response.data.attributes.display_line_items_savings;
    orderDetails.shipmentSavings =
      response.data.attributes.display_shipment_savings;

    orderDetails.orderSavings = response.data.attributes.display_order_savings;

    orderDetails.redemptionPoints =
      response.data.attributes.total_redeem_points;

    orderDetails.subscriptionNumbers =
      response.data.attributes.subscription_numbers;

    orderDetails.parentSubscriptionOrder =
      response.data.attributes.parent_subscription_order;

    orderDetails.iTeroPaymentMethod = response.included?.find(
      includedItem => includedItem.type === 'proposal'
    )?.attributes?.order_payment_token;

    orderDetails.contractDocumentUrl = response.included?.find(
      includedItem => includedItem.type === 'proposal'
    )?.attributes?.contract_document_url;

    const shipments = response.data.relationships.shipments?.data;
    if (shipments && shipments.length > 0) {
      orderDetails.orderTracking = this.buildOrderTracking(response);
    } else {
      orderDetails.orderTracking = [];
    }

    return orderDetails;
  }

  buildProposalForClient(response: IteroOrder): IteroOrderDetails {
    const proposalResponse = response.data.attributes.parsed_response;

    const proposalDetails = proposalResponse.proposal;
    const proposalLineItemDetails = proposalResponse.line_items;

    const billingAddressId =
      response.data.relationships.billing_address.data.id;

    const billingAddress = response.included?.filter(
      includedItem =>
        includedItem.type === 'address' && includedItem.id === billingAddressId
    )[0] as AccountAddressAttr | undefined;

    const shippingAddressId =
      response.data.relationships.shipping_address.data.id;
    const shippingAddress = response.included?.filter(
      includedItem =>
        includedItem.type === 'address' && includedItem.id === shippingAddressId
    )[0] as AccountAddressAttr | undefined;

    const creditCardDetails = response.included?.filter(
      includedItem => includedItem.type === 'credit_card'
    )[0];

    const extractLast4Digits = (cardNumber: string) => cardNumber.split('-')[1];

    /**
     * Handles the payment token by extracting the last 4 digits if available.
     * If the last 4 digits cannot be extracted, it returns the original payment token.
     *
     * @param {string} paymentToken - The payment token which can be one of the following types:
     * - Wire/Check
     * - 3rd Party Financing
     * - Alphanumeric string ending with a hyphen and 4 digits (e.g., GV79D6M63DQHC8V5-1142)
     * @returns {string} The last 4 digits of the payment token if available, otherwise the original payment token.
     */
    const handlePaymentToken = (paymentToken: string) => {
      const last4Digits = extractLast4Digits(paymentToken);

      return last4Digits ? last4Digits : paymentToken;
    };

    return {
      proposalId: proposalDetails.sfid,
      proposalName: proposalDetails.proposal_name,
      displayTotal: proposalDetails.display_total,
      displayAdjustmentTotal: proposalDetails.display_adjustment_total,
      displayTotalAfterAdjustments:
        proposalDetails.display_total_after_adjustments,
      billAddressId: proposalDetails.bill_address_id,
      shipAddressId: proposalDetails.ship_address_id,
      scannerFundingSource: proposalDetails.funding_source,
      scannerSubFundingSource: proposalDetails.sub_funding_source,
      financingCompany: proposalDetails.financing_company,
      existingScannerSerialNumber: proposalDetails.serial_number,
      billingAddress: this.buildAddress(billingAddress),
      shippingAddress: this.buildAddress(shippingAddress),
      orderDate: response.data.attributes.completed_at,
      creditCardLast4Digits: handlePaymentToken(
        response.data.attributes.order_payment_token
      ),
      subscriptionCardLast4Digits: handlePaymentToken(
        response.data.attributes.order_subscription_token
      ),
      iTeroPaymentMethod: response.data.attributes.order_payment_token,
      iTeroSubscriptionMethod:
        response.data.attributes.order_subscription_token,
      contractDocumentUrl: response.data.attributes.contract_document_url,
      creditCardType: creditCardDetails?.attributes?.cc_type,
      customerNeed: proposalDetails.customer_need,
      processType: proposalDetails.process_type,
      displayTax: '',
      displayTotalAfterTax: '',
      promotion: proposalDetails.promotion,
      saleType: proposalDetails.sale_type,
      estimatedMonthlyPayment: proposalDetails.estimated_monthly_payment,
      entityName: proposalDetails.entity_name,
      tradeInSerialNumber: proposalDetails.trade_in_serial_number, // for EMEA proposals, in Trade-In Details section of the terms and conditions modal, NOT THE SAME AS existingScannerSerialNumber which is shown in the Promotion Specific Details section of the terms and conditions modal
      opportunityId: proposalDetails.opportunity_id,
      opportunityOwnerName: proposalDetails.opportunity_owner_name,
      opportunityNumber: proposalDetails.opportunity_number,
      scannerModel: proposalDetails.scanner_model,
      servicePlanDiscount: proposalDetails.service_plan_discount,
      lineitems: proposalLineItemDetails.map(lineItem => {
        return {
          name: lineItem.name,
          quantity: lineItem.quantity,
          displayItemUnitPrice: lineItem.list_price,
          displayItemTotal: lineItem.net_price,
          promotion: {
            programName: lineItem.promotion?.program_name,
            programCode: lineItem.promotion?.program_code,
            amount: lineItem.promotion?.promotion_amount,
          },
          softwareType: lineItem.scanner_type,
          serviceTerm: lineItem.service_type,
          servicePlan: lineItem.service_plan,
          sellingTerm: lineItem.selling_term,
          description: lineItem.description,
          itemSequence: lineItem.item_sequence,
          netUnitPrice: lineItem.net_unit_price,
          sku: lineItem.sku,
          options: lineItem?.options
            ?.filter(option => !option.service_type || !option.service_plan)
            .map(option => {
              return {
                name: option.name,
                quantity: option.quantity,
                displayItemUnitPrice: option.list_price,
                displayItemTotal: option.net_price,
              };
            }),
          subscriptions: lineItem?.options
            ?.filter(option => option.service_type || option.service_plan)
            .map(option => {
              return {
                name: option.name,
                quantity: option.quantity,
                displayItemUnitPrice: option.list_price,
                displayItemTotal: option.net_price,
                softwareType: option?.scanner_type,
                serviceTerm: option?.service_type,
                servicePlan: option?.service_plan,
                promotionCode: option?.promotion_code,
                adjustmentAmount: option?.adjustment_amount,
              };
            }),
        };
      }),
    };
  }

  buildAddress(address: AccountAddressAttr | undefined) {
    if (address) {
      return {
        id: address.id,
        salesforceId: address.attributes.salesforce_id,
        firstname: address.attributes.firstname,
        lastname: address.attributes.lastname,
        address1: address.attributes.address1,
        address2: address.attributes.address2,
        address3: address.attributes.address3,
        city: address.attributes.city,
        zipcode: address.attributes.zipcode,
        stateName: address.attributes.state_name,
        countryName: address.attributes.country_name,
        countryIso: address.attributes.country_iso,
        phone: address.attributes.phone,
        isDefaultBilling: address.attributes.is_default_billing,
        isDefaultShipping: address.attributes.is_default_shipping,
        billTo: address.attributes.bill_to,
        shipTo: address.attributes.ship_to,
        mailingName: address.attributes.mailing_name,
        translatedName: address.attributes.translated_name,
        translatedMailingName: address.attributes.translated_mailing_name,
      };
    } else {
      return {
        id: '',
        salesforceId: '',
        firstname: '',
        lastname: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        zipcode: '',
        stateName: '',
        countryName: '',
        countryIso: '',
        phone: '',
        isDefaultBilling: false,
        isDefaultShipping: false,
        billTo: false,
        shipTo: false,
        mailingName: '',
        translatedName: '',
        translatedMailingName: '',
      };
    }
  }

  buildLineItems(response: IOrder) {
    const orderLineitems: OrderLineItem[] = [];
    const lineItems = response.included?.filter(
      includedItem => includedItem.type === 'line_item'
    );
    const variants = response.included?.filter(
      includedItem => includedItem.type === 'variant'
    );
    lineItems?.forEach(lineItem => {
      orderLineitems.push(this.buildLineItem(lineItem, variants));
    });

    return orderLineitems;
  }

  buildLineItem(lineItem: any, variants: any[] | undefined) {
    const orderLineItem: OrderLineItem = {
      name: '',
      quantity: '',
      price: '',
      total: '',
      imageUrl: '',
      id: '',
      discontinueOn: '',
      isCustomizable: false,
      points: '',
      isSubscribed: false,
      subscriptionFrequencyTitle: '',
      discountPercentage: '',
      couponCode: '',
      subscriptionNumber: '',
      parentSubscriptionOrder: false,
    };

    const variant_id = lineItem.relationships.variant.data.id;
    const variant = variants?.find(variant => variant_id === variant.id);
    orderLineItem.discontinueOn = variant?.attributes?.discontinue_on;
    orderLineItem.name = lineItem.attributes.name;
    orderLineItem.quantity = lineItem.attributes.quantity;
    orderLineItem.price = lineItem.attributes.display_price;
    orderLineItem.total =
      lineItem.attributes.is_having_subscription_adjustments ||
      lineItem.attributes.is_having_bulk_discount_adjustments ||
      lineItem.attributes.is_having_promo_code_adjustments ||
      lineItem.attributes.is_having_dso_discount_adjustments
        ? lineItem.attributes.display_total_after_bulk_discount
        : lineItem.attributes.display_total;
    orderLineItem.imageUrl = this.getThumbnailUrl(
      lineItem.attributes.primary_image_url
    );
    orderLineItem.id = lineItem.id;
    orderLineItem.points = lineItem.attributes.redeem_points;
    orderLineItem.isSubscribed = lineItem.attributes.is_subscribed;
    orderLineItem.parentSubscriptionOrder =
      lineItem.attributes.parent_subscription_order;
    orderLineItem.subscriptionFrequencyTitle =
      lineItem.attributes.subscription_title;
    orderLineItem.subscriptionNumber = lineItem.attributes.subscription_number;
    orderLineItem.discountPercentage = lineItem.attributes.discount_percentage;
    orderLineItem.couponCode = lineItem.attributes.coupon_code;
    return orderLineItem;
  }

  buildOrderHistoryForClient(
    response: IOrdersWithMetaData,
    currentPageNumber?: number
  ) {
    const orderHistory: {
      orders: OrderRowData[];
      count: number;
      totalCount: number;
      totalPages: number;
      currentPageNumber?: number;
      shipmentStatusList: string[];
    } = {
      orders: [],
      count: 0,
      totalCount: 0,
      totalPages: 0,
      currentPageNumber: currentPageNumber,
      shipmentStatusList: [],
    };

    const rawOrders = response.data;
    const shipments = response.included;
    const shipmentStatuses = response.meta.shipment_statuses;

    rawOrders.forEach(rawOrder => {
      const orderObj: OrderRowData = {
        number: '',
        completedAt: new Date(),
        shippedAt: [],
        tracking: [],
        trackingNumber: [],
        displayTotal: '',
        status: undefined,
        subscriptionNumbers: [],
        parentSubscriptionOrder: false,
      };
      orderObj.number = rawOrder.attributes.number;
      orderObj.completedAt = rawOrder.attributes.completed_at;
      orderObj.subscriptionNumbers = rawOrder.attributes.subscription_numbers;
      orderObj.parentSubscriptionOrder =
        rawOrder.attributes.parent_subscription_order;

      const shipmentIds = rawOrder.relationships.shipments.data.map(
        shipment => shipment.id
      );
      shipmentIds.forEach(shipmentId => {
        const shipped_at = shipments?.filter(
          shipment => shipment.id === shipmentId
        )[0].attributes.shipped_at;
        const trackingUrl = shipments?.filter(
          shipment => shipment.id === shipmentId
        )[0].attributes.tracking_url;
        const trackingId = shipments?.filter(
          shipment => shipment.id === shipmentId
        )[0].attributes.tracking;
        if (shipped_at) {
          orderObj.shippedAt?.push(shipped_at);
        }
        if (trackingUrl) {
          orderObj.tracking?.push(trackingUrl);
        }
        if (trackingId) {
          orderObj.trackingNumber?.push(trackingId);
        }
      });

      orderObj.displayTotal = rawOrder.attributes.display_total;
      orderObj.status = rawOrder.attributes.shipment_state;
      orderHistory.orders.push(orderObj);
    });

    orderHistory.count = response.meta?.count;
    orderHistory.totalCount = response.meta?.total_count;
    orderHistory.totalPages = response.meta?.total_pages;
    orderHistory.shipmentStatusList = shipmentStatuses ?? [];

    const sortedOrders = [...orderHistory.orders].sort(
      (order1, order2) =>
        Number(new Date(order2.completedAt)) -
        Number(new Date(order1.completedAt))
    );
    const sortedOrderHistory = { ...orderHistory, orders: sortedOrders };

    return sortedOrderHistory;
  }

  buildITeroOrderHistoryForClient(
    response: IteroOrdersWithMetaData,
    currentPageNumber?: number
  ) {
    const orderHistory: {
      orders: IteroOrderRowData[];
      count: number;
      totalCount: number;
      totalPages: number;
      currentPageNumber?: number;
      shipmentStatusList: string[];
    } = {
      orders: [],
      count: 0,
      totalCount: 0,
      totalPages: 0,
      currentPageNumber: currentPageNumber,
      shipmentStatusList: [],
    };

    const rawOrders = response.data;

    rawOrders.forEach(rawOrder => {
      const orderObj: IteroOrderRowData = {
        proposalName: '',
        proposalSalesforceId: '',
        completedAt: '',
        displayTotal: '',
        shippedAt: '',
        trackingNumber: '',
        status: '',
      };
      orderObj.proposalSalesforceId = rawOrder.attributes.salesforce_id;
      orderObj.proposalName = rawOrder.attributes.name;
      orderObj.completedAt = rawOrder.attributes.completed_at;
      orderObj.shippedAt = rawOrder.attributes.shipped_at ?? '';
      orderObj.trackingNumber = rawOrder.attributes.tracking_number ?? '';

      orderObj.displayTotal = rawOrder.attributes.display_total_with_currency;
      orderObj.status = rawOrder.attributes.status;
      orderHistory.orders.push(orderObj);
    });

    orderHistory.count = response.meta?.count;
    orderHistory.totalCount = response.meta?.total_count;
    orderHistory.totalPages = response.meta?.total_pages;

    const sortedOrders = [...orderHistory.orders].sort(
      (order1, order2) =>
        Number(new Date(order2.completedAt)) -
        Number(new Date(order1.completedAt))
    );
    const sortedOrderHistory = { ...orderHistory, orders: sortedOrders };

    return sortedOrderHistory;
  }

  buildOrderTracking(response: IOrder) {
    const orderShipments = response.included?.filter(
      includedItem => includedItem.type === 'shipment'
    );
    const shipmentManifest = orderShipments?.[0]?.attributes?.manifest;

    if (!orderShipments || !shipmentManifest) {
      return [];
    } else {
      const orderTracking = orderShipments?.map(shipment => {
        return {
          trackingNumber: shipment.attributes.tracking,
          trackingUrl: shipment.attributes.tracking_url,
          trackingState: shipment.attributes.state,
          lineItems: this.buildTrackingLineItemList(
            response,
            shipment.attributes.manifest
          ),
        };
      });

      return orderTracking;
    }
  }
  buildTrackingLineItemList(response: IOrder, manifest: any) {
    const allLineItems = this.buildLineItems(response);
    const findManifestLineItem = (manifestLineItem: any) =>
      allLineItems.find(
        lineItem => lineItem.id === manifestLineItem.line_item.id.toString()
      );

    const trackingLineItemList = manifest.map((manifestItem: any) => {
      return {
        id: manifestItem.line_item.id,
        imageUrl: findManifestLineItem(manifestItem)?.imageUrl ?? '',
        name: findManifestLineItem(manifestItem)?.name ?? '',
        quantity: findManifestLineItem(manifestItem)?.quantity ?? '',
        price: findManifestLineItem(manifestItem)?.price ?? '',
        total: findManifestLineItem(manifestItem)?.total ?? '',
      };
    });

    return trackingLineItemList;
  }

  buldAccountDetails(response: AccountResponse) {
    const accountDetails = {
      id: response.data.id,
      optIn: response.data.attributes.opt_in,
      email: response.data.attributes.email,
      licenseNumber: response.data.attributes.license_number,
      licenseState: response.data.attributes.license_state,
      phone: response.data.attributes.phone,
      isDso: response.data.attributes.is_dso,
      isDsoMandatoryCardPaymentRequired:
        response.data.attributes.is_dso_mandatory_card_payment_required,
    };
    return accountDetails;
  }
  getThumbnailUrl(productImage: string | undefined) {
    return productImage || '/images/noimage.jpeg';
  }

  buildDownloadHistoryForClient(
    response: DownloadServerResponse,
    currentPageNumber?: number
  ) {
    const clientDownloadHistory: {
      downloads: DownloadClientResponse[];
      count: number;
      totalCount: number;
      totalPages: number;
      currentPageNumber?: number;
    } = {
      downloads: [],
      count: 0,
      totalCount: 0,
      totalPages: 0,
      currentPageNumber: currentPageNumber,
    };

    if (!response) {
      return clientDownloadHistory;
    }

    response.data.downloads.forEach(download =>
      clientDownloadHistory.downloads.push({
        status: download.status ?? 'system approved',
        id: download.dh_project_id,
        projectName: download.dh_project_name,
        productName: download.product_name,
        visibleName: download.dh_project_name ?? download.product_name,
        downloadCount: download.download_count,
        sku: download.sku,
        createdAt: download.created_at,
      })
    );

    clientDownloadHistory.count = response.data.meta.count;
    clientDownloadHistory.totalCount = response.data.meta.total_count;
    clientDownloadHistory.totalPages = response.data.meta.total_pages;

    return clientDownloadHistory;
  }
}
